import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { XMarkIcon, SparklesIcon, CheckCircleIcon, CalendarIcon, ClockIcon } from '@heroicons/react/24/outline'
import { nutritionAPI } from '../../services/api'
import MealTargetSelector from './MealTargetSelector'

export default function ManualFoodTextEntry({
  onClose,
  selectedMealType: initialMealType,
  selectedDate: initialDate,
  onSuccess
}) {
  const { t } = useTranslation()
  const [description, setDescription] = useState('')
  const [mode, setMode] = useState('auto') // 'auto' or 'partial'
  const [mealType, setMealType] = useState(initialMealType || 'breakfast')
  const [logDate, setLogDate] = useState(initialDate || new Date().toISOString().split('T')[0])
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [analysisResult, setAnalysisResult] = useState(null)
  const [existingMeals, setExistingMeals] = useState([])
  const [selectedMealId, setSelectedMealId] = useState(null)
  const [error, setError] = useState(null)

  const handleAnalyze = async () => {
    if (!description.trim()) {
      setError(t('nutrition.errors.emptyDescription'))
      return
    }

    setIsAnalyzing(true)
    setError(null)

    try {
      const now = new Date()
      const mealTime = now.toISOString()

      const response = await nutritionAPI.analyzeFoodText({
        description: description.trim(),
        meal_type: mealType,
        meal_time: mealTime,
        log_date: logDate,
        mode: mode
      })

      setAnalysisResult(response.data)
      console.log('Text analysis result:', response.data)

      // Fetch existing meals for this date+type to offer add-to-existing choice
      try {
        const mealsResponse = await nutritionAPI.getMealsByDate(logDate)
        const mealsOfType = (mealsResponse.data?.meals || []).filter(
          (m) => m.meal_type?.toLowerCase() === mealType.toLowerCase()
        )
        setExistingMeals(mealsOfType)
        setSelectedMealId(null) // Default: create new
      } catch {
        // Non-critical — if fetch fails, just default to create new
        setExistingMeals([])
      }
    } catch (err) {
      console.error('Failed to analyze text:', err)
      setError(err.response?.data?.detail || t('nutrition.errors.analyzeTextFailed'))
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleSave = async () => {
    if (!analysisResult) return

    setIsSaving(true)
    setError(null)

    try {
      const now = new Date()
      const mealTime = now.toISOString()

      await nutritionAPI.saveTextFoodEntry({
        analysis_data: {
          foods_identified: analysisResult.foods_identified,
          meal_total: analysisResult.meal_total
        },
        meal_type: mealType,
        meal_time: mealTime,
        log_date: logDate,
        meal_id: selectedMealId
      })

      console.log('Text food entry saved successfully')

      // Call success callback
      if (onSuccess) {
        onSuccess()
      }

      // Close modal
      onClose()
    } catch (err) {
      console.error('Failed to save text entry:', err)
      setError(err.response?.data?.detail || t('nutrition.errors.saveEntryFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setAnalysisResult(null)
    setDescription('')
    setError(null)
    setExistingMeals([])
    setSelectedMealId(null)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-container rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-surface-container border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">
            {t('nutrition.manualEntry.title')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {!analysisResult ? (
            <>
              {/* Date and Meal Type Selection */}
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                {/* Date Picker */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <CalendarIcon className="inline h-4 w-4 mr-1" />
                    {t('nutrition.manualEntry.date')}
                  </label>
                  <input
                    type="date"
                    value={logDate}
                    onChange={(e) => setLogDate(e.target.value)}
                    className="w-full px-3 py-2 focus:outline-none focus:ring-2"
                  />
                </div>

                {/* Meal Type Selector */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <ClockIcon className="inline h-4 w-4 mr-1" />
                    {t('nutrition.manualEntry.mealType')}
                  </label>
                  <select
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                    className="w-full px-3 py-2 focus:outline-none focus:ring-2"
                  >
                    <option value="breakfast">{t('nutrition.mealTypes.breakfast')}</option>
                    <option value="lunch">{t('nutrition.mealTypes.lunch')}</option>
                    <option value="dinner">{t('nutrition.mealTypes.dinner')}</option>
                    <option value="snack">{t('nutrition.mealTypes.snack')}</option>
                  </select>
                </div>
              </div>

              {/* Entry mode selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('nutrition.manualEntry.entryMode')}
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="auto"
                      checked={mode === 'auto'}
                      onChange={(e) => setMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      {t('nutrition.manualEntry.aiEstimateLabel')}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="partial"
                      checked={mode === 'partial'}
                      onChange={(e) => setMode(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      {t('nutrition.manualEntry.withValuesLabel')}
                    </span>
                  </label>
                </div>
              </div>

              {/* Text input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('nutrition.manualEntry.foodDescription')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    mode === 'auto'
                      ? t('nutrition.manualEntry.placeholderAuto')
                      : t('nutrition.manualEntry.placeholderPartial')
                  }
                  rows={3}
                  className="w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {mode === 'auto'
                    ? t('nutrition.manualEntry.aiWillEstimate')
                    : t('nutrition.manualEntry.aiFillGaps')}
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {error}
                </div>
              )}

              {/* Analyze button */}
              <button
                onClick={handleAnalyze}
                disabled={isAnalyzing || !description.trim()}
                className="w-full bg-indigo-600 text-on-neutral py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <SparklesIcon className="h-5 w-5" />
                {isAnalyzing ? t('nutrition.manualEntry.analyzing') : t('nutrition.manualEntry.analyzeFood')}
              </button>
            </>
          ) : (
            <>
              {/* Analysis Results */}
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t('nutrition.manualEntry.analysisComplete')}
                  </h3>
                </div>

                {/* Confidence and warnings */}
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{t('nutrition.manualEntry.confidence')}:</strong> {(analysisResult.confidence * 100).toFixed(0)}%
                  </p>
                  {analysisResult.warnings && analysisResult.warnings.length > 0 && (
                    <div className="mt-2">
                      <p className="text-sm font-medium text-blue-800">{t('nutrition.manualEntry.notes')}:</p>
                      <ul className="list-disc list-inside text-sm text-blue-700 mt-1">
                        {analysisResult.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Identified Foods */}
                <div className="space-y-3 mb-4">
                  <h4 className="font-medium text-gray-900">{t('nutrition.manualEntry.identifiedFoods')}</h4>
                  {analysisResult.foods_identified.map((food, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{food.name}</p>
                          {food.brand && (
                            <p className="text-sm text-gray-500">{food.brand}</p>
                          )}
                          <p className="text-sm text-gray-600">
                            {food.quantity} {food.quantity_unit}
                          </p>
                        </div>
                        <span className="text-xs bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                          {t('nutrition.manualEntry.confident', { percent: (food.confidence * 100).toFixed(0) })}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-gray-600">{t('nutrition.manualEntry.calories')}</span>
                          <span className="ml-1 font-medium">
                            {food.nutritional_summary.calories?.toFixed(0)} kcal
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('nutrition.manualEntry.protein')}</span>
                          <span className="ml-1 font-medium">
                            {food.nutritional_summary.protein_g?.toFixed(1)}g
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('nutrition.manualEntry.carbs')}</span>
                          <span className="ml-1 font-medium">
                            {food.nutritional_summary.carbohydrates_g?.toFixed(1)}g
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">{t('nutrition.manualEntry.fat')}</span>
                          <span className="ml-1 font-medium">
                            {food.nutritional_summary.fat_g?.toFixed(1)}g
                          </span>
                        </div>
                      </div>

                      {food.notes && (
                        <p className="mt-2 text-xs text-gray-500 italic">{food.notes}</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Meal Total */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-indigo-900 mb-2">{t('nutrition.manualEntry.mealTotal')}</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-indigo-700">{t('nutrition.manualEntry.calories')}</span>
                      <span className="ml-2 font-bold text-indigo-900">
                        {analysisResult.meal_total.calories?.toFixed(0)} kcal
                      </span>
                    </div>
                    <div>
                      <span className="text-indigo-700">{t('nutrition.manualEntry.protein')}</span>
                      <span className="ml-2 font-bold text-indigo-900">
                        {analysisResult.meal_total.protein_g?.toFixed(1)}g
                      </span>
                    </div>
                    <div>
                      <span className="text-indigo-700">{t('nutrition.manualEntry.carbs')}</span>
                      <span className="ml-2 font-bold text-indigo-900">
                        {analysisResult.meal_total.carbohydrates_g?.toFixed(1)}g
                      </span>
                    </div>
                    <div>
                      <span className="text-indigo-700">{t('nutrition.manualEntry.fat')}</span>
                      <span className="ml-2 font-bold text-indigo-900">
                        {analysisResult.meal_total.fat_g?.toFixed(1)}g
                      </span>
                    </div>
                  </div>
                </div>

                {/* Meal target selection — only shown when existing meals exist */}
                {existingMeals.length > 0 && (
                  <div className="mb-4">
                    <MealTargetSelector
                      existingMeals={existingMeals}
                      selectedMealId={selectedMealId}
                      onSelect={setSelectedMealId}
                    />
                  </div>
                )}

                {/* Error message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300"
                  >
                    {t('nutrition.manualEntry.startOver')}
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex-1 bg-indigo-600 text-on-neutral py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {isSaving ? t('nutrition.manualEntry.saving') : t('nutrition.manualEntry.saveToDiary')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
